import { createDoc } from "../jspdf/createDoc";
import { createCursor, ensureSpace, getPageSize } from "../jspdf/cursor";
import { addPageNumbers, drawPageChrome } from "../jspdf/pageChrome";
import { defaultGridStyles, drawAutoTable } from "../jspdf/tables";
import { amountUsdToWords } from "../utils/numberToWords";

function formatDateDDMMYYYY(date) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Draws the fixed first-page layout (exporter/consignee/meta/shipping).
 * Returns a suggested Y to start the items table.
 */
function drawInvoiceHeader(doc, model) {
  const { width: pageWidth } = getPageSize(doc);
  const x0 = 10;
  const y0 = 45;

  // Border + title (matches your existing look)
  drawPageChrome(doc, { title: "COMMERCIAL INVOICE", border: true, titleY: 40, borderRect: { x: x0, y: y0, w: 190, h: 250 } });

  // Left: Exporter box
  doc.rect(x0, y0, 90, 36);
  doc.setFontSize(8);
  doc.text("Exporter", x0 + 3, y0 + 4);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(model.exporter.name || "", x0 + 3, y0 + 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(doc.splitTextToSize(model.exporter.address || "", 80), x0 + 3, y0 + 14);
  doc.text(doc.splitTextToSize(model.exporter.email || "", 80), x0 + 3, y0 + 23);
  doc.text(doc.splitTextToSize(model.exporter.contactPerson || "", 80), x0 + 3, y0 + 28);
  doc.text(doc.splitTextToSize(model.exporter.phone || "", 80), x0 + 3, y0 + 32);

  // Left: Consignee box
  doc.rect(x0, y0 + 36, 90, 34);
  doc.setFontSize(8);
  doc.text("Consignee", x0 + 3, y0 + 40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(model.consignee.name || "", x0 + 3, y0 + 45);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(doc.splitTextToSize(model.consignee.address || "", 80), x0 + 3, y0 + 49);
  doc.text(model.consignee.email || "", x0 + 3, y0 + 58);
  doc.text(model.consignee.contactPerson || "", x0 + 3, y0 + 62);
  doc.text(model.consignee.phone || "", x0 + 3, y0 + 66);

  // Right: meta boxes
  const xr = x0 + 90;
  const yr = y0;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.rect(xr, yr, 40, 7);
  doc.setFontSize(8);
  doc.text("Invoice No:", xr + 2, yr + 5);
  doc.setFontSize(9);
  doc.text(model.meta.invNo || "", xr + 19, yr + 5);

  doc.rect(xr, yr + 7, 40, 9);
  doc.setFontSize(9);
  doc.text("Invoice Date:", xr + 2, yr + 13);
  doc.text(formatDateDDMMYYYY(model.meta.invDate), xr + 22, yr + 13);

  doc.rect(xr + 40, yr, 60, 16);
  doc.setFontSize(8);
  doc.text("IE Code:", xr + 43, yr + 10);
  doc.setFontSize(10);
  doc.text(model.meta.ieCode || "", xr + 58, yr + 10);

  doc.rect(xr, yr + 16, 100, 12);
  doc.setFontSize(8);
  doc.text("Buyer's Order No:", xr + 2, yr + 20);
  doc.setFontSize(10);
  doc.text(model.meta.ordNo || "", xr + 4, yr + 25);
  doc.setFontSize(8);
  doc.text("Dated:", xr + 40, yr + 23);
  doc.setFontSize(11);
  doc.text(formatDateDDMMYYYY(model.meta.ordDate), xr + 52, yr + 23);

  // Buyer / country boxes
  doc.setFontSize(8);
  doc.rect(xr, yr + 28, 100, 26);
  doc.text("Buyer (if other than consignee)", xr + 2, yr + 34);
  doc.setFontSize(8);
  doc.text(doc.splitTextToSize(model.carrier.buyerId || "", 95), xr + 2, yr + 42);
  doc.rect(xr, yr + 54, 100, 9);
  doc.text("Country of Manufacture :", xr + 2, yr + 59);
  doc.text(model.carrier.countryManufacture || "", xr + 50, yr + 61);
  doc.rect(xr, yr + 63, 100, 0);
  doc.text("Country of Export :", xr + 2, yr + 68);
  doc.text(model.carrier.countryExport || "", xr + 50, yr + 68);
  doc.rect(xr, yr + 73, 100, 9);
  doc.text("Country of Final Destination :", xr + 2, yr + 78);
  doc.text(model.carrier.countryDestination || "", xr + 50, yr + 78);

  // Payment terms
  doc.rect(xr, yr + 82, 100, 23);
  doc.text("Terms of delivery and payment:", xr + 2, yr + 86);
  doc.text(doc.splitTextToSize(model.payment.termsDeliveryPayment || "", 90), xr + 2, yr + 91);

  //Shipping / carrier
  let x = x0;
  let y = 117;
  doc.setFontSize(8);
  doc.rect(x, y - 2, 35, 13);
  doc.text("Pre-Carriage By:", x + 2, y + 4);
  doc.text(model.shipping.preCarriageBy || "", x + 5, y + 9);

  doc.rect(x + 35, y - 2, 55, 13);
  doc.setFontSize(8);
  doc.text("Place of reciept by pre-carrier:", x + 37, y + 4);
  doc.setFontSize(10);
  doc.text(doc.splitTextToSize(model.shipping.placeReceiptByPreCarrier || "", 50), x + 37, y + 9);

  doc.rect(x, y + 11, 35, 11);
  doc.setFontSize(8);
  doc.text("Vessel/flight No:", x + 2, y + 15);
  doc.text(model.shipping.vesselOrFlightNo || "", x + 2, y + 19);
  doc.setFontSize(8);
  doc.rect(x + 35, y + 11, 55, 11);
  doc.text("Port of loading:", x + 37, y + 15);
  doc.text(model.shipping.portLoading || "", x + 37, y + 19);

  doc.rect(x, y + 22, 35, 11);
  doc.text("Port of Discharge:", x + 2, y + 26);
  doc.text(model.shipping.portDischarge || "", x + 2, y + 31);

  doc.rect(x + 35, y + 22, 55, 11);
  doc.text("Final Destination:", x + 37, y + 26);
  doc.text(model.shipping.finalDestination || "", x + 37, y + 31);

  // Table start after shipping boxes
  return y + 37;
}

function drawTotalsAndDeclaration(doc, model, cursor) {
  const { width: pageWidth } = getPageSize(doc);
  const x = cursor.marginLeft;

  // Totals block ~18mm
  ensureSpace(doc, cursor, 20, {
    nextPageStartY: 55,
    onNewPage: () => {
      // If totals move to a new page, redraw only the border (no title on later pages).
      drawPageChrome(doc, { border: true, titleY: 40, borderRect: { x: 10, y: 45, w: 190, h: 250 } });
    },
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Total Quantity", 93, cursor.y);
  doc.text(String(model.totals.totalQty), 123, cursor.y);
  
  // Calculate total amount including freight
  const freightAmount = Number(model.payment.freight) || 0;
  const totalAmountWithFreight = model.totals.totalAmountUsd + freightAmount;
  
  doc.text("Total Amount (USD):", 144, cursor.y);
  doc.text(model.totals.totalAmountUsd.toFixed(2), 183, cursor.y);
  doc.text("Freight:", 148, cursor.y + 7);
  doc.text(model.payment.freight || "", 186, cursor.y + 7);
  doc.text("Total Amount:", 144, cursor.y + 14);
  doc.text(totalAmountWithFreight.toFixed(2), 183, cursor.y + 14);

  // Amount in words row
  doc.rect(x + 2, cursor.y + 17, 185, 8);
  const amountWords = amountUsdToWords(totalAmountWithFreight);
  doc.setFont("helvetica", "normal");
  doc.text(`Total Amount in Words: ${amountWords}`, 24, cursor.y + 23);
  cursor.y += 27;

  // Declaration block ~55mm
  ensureSpace(doc, cursor, 60, {
    nextPageStartY: 55,
    onNewPage: () => {
      // If declaration moves to a new page, redraw only the border (no title on later pages).
      drawPageChrome(doc, { border: true, titleY: 40, borderRect: { x: 10, y: 45, w: 190, h: 250 } });
    },
  });
  const y = cursor.y;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.rect(x, y + 10, 120, 40);
  doc.text("Declaration", x + 5, y + 15);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const declarationText =
    "We declare that the invoice shows the actual price of goods described and that all particulars are true and correct.";
  const lines = doc.splitTextToSize(declarationText, 110);
  let yOffset = y + 22;
  lines.forEach((line) => {
    if (yOffset <= y + 40) {
      doc.text(line, x + 5, yOffset);
      yOffset += 5;
    }
  });
  doc.text("We intend to claim rewards under", x + 5, y + 37);
  doc.setFontSize(10);
  doc.text("RODTEP Scheme", x + 5, y + 45);

  doc.rect(x + 120, y + 10, 70, 25);
  doc.setFontSize(9);
  doc.text("Company's PAN : ASAPA6292J", x + 122, y + 16);
  doc.text("Company's GST : 36ASAPA6292JIZQ", x + 122, y + 21);
  doc.text(`Company's LUT : ${model.payment.companyLUT || "ASAPA54fYY2J"}`, x + 122, y + 26);

  // Signature frame (no bottom line, matches your existing style)
  doc.line(x + 120, y + 35, x + 190, y + 35);
  doc.line(x + 120, y + 35, x + 120, y + 50);
  doc.line(x + 190, y + 35, x + 190, y + 50);
  doc.text("Signature and Co. Stamp", x + 135, y + 48);
  doc.line(x + 120, y + 50, x + 190, y + 50);

  cursor.y = y + 55;
  // keep doc state sane
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("", pageWidth - 1, 1); // no-op line to avoid some PDF viewers font glitches
}

export function generateInvoicePdfDataUrl(model) {
  const doc = createDoc();
  const cursor = createCursor({ marginLeft: 10, marginRight: 10, marginTop: 10, marginBottom: 25, y: 10 });

  const tableStartY = drawInvoiceHeader(doc, model);

  const headers = [["S.No", "Description", "HSN Code", "Qty", "Rate (USD)", "Amount (USD)"]];
  const body = model.items.map((it, idx) => [
    idx + 1,
    it.description || "N/A",
    it.hsnCode || "N/A",
    Number(it.quantity || 0),
    Number(it.rateUsd || 0).toFixed(2),
    Number(it.amountUsd || 0).toFixed(2),
  ]);

  const grid = defaultGridStyles({ fontSize: 8, cellPadding: 3 });

  drawAutoTable(doc, {
    startY: tableStartY,
    head: headers,
    body,
    margin: { left: 15, right: 15, top: 55, bottom: 25 }, // top only affects new pages; keep away from title/border, bottom for footer space
    ...grid,
    columnStyles: {
      1: { cellWidth: 60, fontStyle: "normal" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "center" },
    },
    willDrawPage: (data) => {
      // For additional pages of the item list, redraw only the border without the title.
      // The first page header already draws the title "COMMERCIAL INVOICE".
      if (data.pageNumber === 1) return;
      drawPageChrome(doc, { border: true, titleY: 40, borderRect: { x: 10, y: 45, w: 190, h: 250 } });
    },
  });

  // Continue after the items table
  cursor.y = (doc.lastAutoTable?.finalY || tableStartY) + 10;
  drawTotalsAndDeclaration(doc, model, cursor);

  addPageNumbers(doc);
  return doc.output("dataurlstring");
}


